import { OPERATIONS } from '@quadminds/shared/commons';
import { TemperatureStatusEnum } from '@quadminds/shared/tracking-system';
import { ICreateOrderItemDto, ICreateOrderMeasureDto } from './create-order-dto.interface';
import { ITripServiceDto } from './service-type-dto.interface';
import { ICheckpointOrderDto } from './trip-output-dto.interface';
import { TripFinishedStatusEnum, VisitStatus, StepOperationType } from '../enums';

export interface IStepDto {
    type: string;
    icon: string;
    name: string;
    finishTripsInProgress: boolean;
    normalDuration: string;
    maxDuration?: string;
    minDuration?: string;
    zones?: string[];
    action?: string;
    slug?: string;
    completedAt?: string;
    overwritePlanned?: string;
    plannedAt?: string;
    estimatedAt?: string;
    triggeredAt?: string;
    stateDefinition?: string;
    finishTripAfter?: string;
    operationType?: StepOperationType;
    isNonOperative?: boolean;
}

export interface ICurrentStateDto {
    totalDelay: number;
}

export interface ICreateOrderWithTripDto {
    checkpointIndex: number;
    code: string;
    qtn?: string;
    orderItems: ICreateOrderItemDto[];
    orderMeasures?: ICreateOrderMeasureDto[];
    pinRequired?: boolean;
    scheduledDate: string;
}

export interface ICreateCheckpointOrderDto {
    order: ICreateOrderWithTripDto | string;
    code?: string;
    label?: string;
    operation?: OPERATIONS;
}

/**
 * Logistic unit taking over the trip from this checkpoint onwards.
 * Only valid on stopover checkpoints. Omitted fields are inherited from the previous section.
 */
export interface ICheckpointUnitChangeDto {
    carrierId?: string;
    vehicleId?: string;
    driverId?: string;
    semitrailerIds?: string[];
}

export interface ICheckpointDto {
    checkpointDefinitionId?: string;
    type: string;
    stateDefinition?: string;
    transitionStateDefinition?: string;
    name?: string;
    steps?: IStepDto[];
    locationId?: string;
    visitStatus?: VisitStatus;
    orders?: (ICreateCheckpointOrderDto | ICheckpointOrderDto)[];
    traveledMetersAfter?: number;
    theoreticalMetersAfter?: number;
    latitude?: string;
    longitude?: string;
    address?: string;
    plannedAt?: string;
    duration?: string;
    isStopover?: boolean;
    isPlanned?: boolean;
    routeId?: string;
    expectedRouteToArrivePolyline?: string;
    accountId?: string;
    unitChange?: ICheckpointUnitChangeDto;
}

export interface ILocationDto {
    latitude: string;
    longitude: string;
    zones?: string[];
}

export interface IDelayObjectDto {
    delay: number;
    coordinates: ILocationDto;
}

export interface ITriggerOrderDto {
    checkpointIndex: number;
    stepIndex: number;
}

export interface ICreateTripDto {
    organizationId?: string;
    tripDefinition: string;
    delayedYellow?: string;
    delayedRed?: string;
    pendingColor?: string;
    finishedColor?: string;
    scheduledStart: string;
    scheduledFinish?: string;
    realStart?: string;
    realFinish?: string;
    finishReason?: string;
    locale?: string;
    tripLifetime?: string;
    tripStartedLifetime?: string;
    vehicleId?: string;
    driverId?: string;
    /** Second person on board (helper), from the same drivers. */
    companionId?: string | null;
    semitrailerIds?: string[];
    accounts?: string[];
    tripCode: string;
    terminalName?: string;
    currentState?: ICurrentStateDto;
    triggerOrder?: ITriggerOrderDto[];
    checkpoints: ICheckpointDto[];
    meters?: number;
    minPercentage?: number;
    maxPercentage?: number;
    maxDrivingTime?: number;
    restingTime?: number;
    maxDailyDrivingTime?: number;
    drivingTimeWindowTolerance?: number;
    metersWithoutOptions?: number;
    finishOnLastCheckpointStep?: boolean;

    forceShowLastCheckpointNext?: boolean;
    stopped?: boolean;
    drivenTime?: number;
    drivingTimeWindow?: [string, string][];
    delayObject?: IDelayObjectDto[];
    tags?: string[];
    rateId?: string;
    services?: ITripServiceDto[];
    carrierId?: string;
    anticipationTime?: string;
    skipEstimation?: boolean;
    temperature?: {
        value: number;
        lastUpdatedAt: string;
        status?: TemperatureStatusEnum;
    };
    status?: TripFinishedStatusEnum;
    startNextTripOnPrevFinish?: boolean;
}

/**
 * Extended trip DTO for bulk create/update operations.
 * Includes additional flags specific to bulk operations.
 */
export interface ICreateTripForBulkDto extends ICreateTripDto {
    /**
     * If true, clears all custom checkpoint schedules (overwritePlanned)
     * and recalculates plannedAt based on routing from scheduledStart.
     * Only used in bulk operations.
     */
    recalculateSchedules?: boolean;
}
